import { type ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { radius } from '@karehero/llama/theme/radius';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';

// The phone before you get into it: clock, date, and whatever has arrived.
// Device chrome rather than product, like StatusBar and TabBar, because none of
// it belongs to us — the app has not been opened yet.
//
// Charcoal ground: a lock screen is a photo in life, and a flat dark field says
// "this is the phone, not a screen of ours" without pretending to be someone's
// wallpaper.
export const LOCK_TIME = '9:41';
export const LOCK_DATE = 'Tuesday 1 September';

// One notification on the lock screen. Light, translucent and rounded, the way
// every platform draws them, so it reads as the OS delivering something rather
// than as a card in our app.
export const PushNotification = ({
  app,
  title,
  body,
}: {
  app: string;
  title: string;
  body: string;
}) => (
  <Stack
    sx={{
      p: 2,
      bgcolor: 'rgba(255, 255, 255, 0.16)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderRadius: `${radius.lg}px`,
    }}
    spacing={0.5}
  >
    <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} variant='overline'>
      {app}
    </Typography>
    <Typography sx={{ color: 'common.white' }} variant='subtitle2'>
      {title}
    </Typography>
    <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)' }} variant='body2'>
      {body}
    </Typography>
  </Stack>
);

const LockScreen = ({ children }: { children?: ReactNode }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'custom.charcoal',
      // The status bar draws in ink, and this ground is dark.
      color: 'common.white',
      '& .MuiTypography-root': { color: 'inherit' },
    }}
  >
    <StatusBar />

    <Stack sx={{ alignItems: 'center', pt: 6 }} spacing={0.5}>
      <Typography sx={{ opacity: 0.75 }} variant='body1'>
        {LOCK_DATE}
      </Typography>
      <Typography variant='h1'>{LOCK_TIME}</Typography>
    </Stack>

    {/* Notifications sit low, above the thumb, not under the clock. */}
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        px: 2,
        pb: 2,
      }}
    >
      <Stack spacing={1}>{children}</Stack>
    </Box>

    <HomeIndicator />
  </Box>
);

export default LockScreen;
